/**
 * Salão: as mesas abertas, com quem está sentado nelas.
 *
 * O lobby anterior mostrava um contador — "3 disponíveis" — e um botão
 * que enfileirava. Funciona e não mostra ninguém. Aqui a mesa é a
 * unidade: tem uma pessoa, um jogo, um tempo de espera e um valor
 * sugerido, e sentar é escolher aquela pessoa.
 *
 * **O que a mesa não mostra é tão decidido quanto o que ela mostra.** Não
 * há retrospecto do adversário, aproveitamento nem saldo. Escolher a mesa
 * pelo histórico de quem está nela é exatamente como se monta a caçada ao
 * jogador fraco, e a tela não vai dar essa munição.
 *
 * As mesas ocupadas aparecem sem botão. Não dá para entrar nelas, e elas
 * existem por um motivo só: uma sala em que todo mundo já está jogando
 * pareceria vazia se só as livres aparecessem, e "vazio" é a pior
 * primeira impressão que este produto pode dar.
 *
 * **Treino e valendo são duas salas, não um filtro.** É o que o banco já
 * fazia — a fila e as partidas carregam `stake_mode` — e o que a interface
 * passou a mostrar: quem está no treino não aparece para quem está
 * valendo, porque não dá para sentar na mesa dele.
 *
 * Quando falta identidade ou saldo, a sala valendo diz o que fazer em vez
 * de sumir. Esconder a opção deixaria a pessoa sem saber que ela existe;
 * recusar sem explicar perde quem estava a dois cliques de depositar.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

import { Avatar } from '../components/Avatar.tsx';
import {
  BarraDeMesa,
  CamadasDeMesa,
  SeloDeMesa,
  corDoJogo,
  molduraDeMesa,
} from '../components/MesaCard.tsx';
import type { GameType } from '../lib/adapter.ts';
import type { EstadoAposta } from '../lib/conta.ts';
import { GAME_LABEL, money } from '../lib/format.ts';
import {
  type MesaAberta,
  type MesaOcupada,
  type Salao as SalaoDados,
  carregarSalao,
  esperandoHa,
  sentar,
} from '../lib/salao.ts';
import { avisar } from '../lib/avisos.ts';

/** De quanto em quanto tempo o salão é reconsultado. */
const INTERVALO_MS = 4_000;

/**
 * Mesa aberta, esperando alguém sentar.
 *
 * O cartão inteiro é o botão, e não um botão dentro dele. Num alvo deste
 * tamanho, a metade que não clicava era um convite a errar — e no
 * celular, onde o cartão ocupa a largura toda, era a maior parte da tela.
 *
 * Mesa própria não é botão: não há o que fazer nela, e um alvo que não
 * responde ao toque é pior que nenhum alvo.
 */
function MesaLivre({
  mesa,
  agora,
  ocupado,
  onSentar,
}: {
  mesa: MesaAberta;
  agora: number;
  ocupado: boolean;
  onSentar: (hostId: string, gameType: GameType) => void;
}) {
  const conteudo = (
    <>
      <CamadasDeMesa gameType={mesa.gameType} />

      <div className="relative flex h-full flex-col justify-between gap-3 p-5 text-white min-h-[14rem]">
        <SeloDeMesa>{GAME_LABEL[mesa.gameType]}</SeloDeMesa>

        <div className="flex items-center gap-3 min-w-0">
          <Avatar
            avatar={mesa.jogador.avatar}
            cor={mesa.jogador.avatarColor}
            tamanho={44}
            nome={mesa.jogador.displayName}
            // O anel separa o avatar do tabuleiro atras dele; sem ele,
            // personagem de cor parecida com a mesa some no fundo.
            className="outline-2 outline-white/60 shrink-0"
          />
          <div className="min-w-0">
            <p className="font-semibold truncate">{mesa.jogador.displayName}</p>
            <p className="text-sm" style={{ color: 'rgb(255 255 255 / 0.78)' }}>
              esperando {esperandoHa(mesa.esperandoDesde, agora)}
            </p>
          </div>
        </div>

        <div className="flex items-baseline justify-between gap-3">
          <span className="rotulo" style={{ color: 'rgb(255 255 255 / 0.72)' }}>
            Valor sugerido
          </span>
          <span className="money font-semibold">
            {mesa.sugestaoCents === null ? 'a combinar' : money(mesa.sugestaoCents)}
          </span>
        </div>

        <BarraDeMesa comSeta={!mesa.souEu}>
          {mesa.souEu ? 'Sua mesa, aberta' : 'Sentar nesta mesa'}
        </BarraDeMesa>
      </div>
    </>
  );

  const moldura = `${molduraDeMesa({ interativo: !mesa.souEu })} min-h-[14rem] h-full`;
  const tema = { ['--tema' as string]: corDoJogo(mesa.gameType) };

  return (
    <li className="h-full">
      {mesa.souEu ? (
        <div className={moldura} style={tema}>
          {conteudo}
        </div>
      ) : (
        <button
          type="button"
          disabled={ocupado}
          onClick={() => onSentar(mesa.hostId, mesa.gameType)}
          aria-label={`Sentar na mesa de ${mesa.jogador.displayName}`}
          className={moldura}
          style={tema}
        >
          {conteudo}
        </button>
      )}
    </li>
  );
}

/**
 * Mesa em jogo. Mesma moldura, sem hover e sem seta: não há para onde ir.
 */
function MesaEmJogo({ mesa }: { mesa: MesaOcupada }) {
  return (
    <li className="h-full">
      <div
        className={`${molduraDeMesa({ interativo: false })} min-h-[14rem] h-full`}
        style={{ ['--tema' as string]: corDoJogo(mesa.gameType), opacity: 0.86 }}
      >
        <CamadasDeMesa gameType={mesa.gameType} />

        <div className="relative flex h-full flex-col justify-between gap-3 p-5 text-white min-h-[14rem]">
          <SeloDeMesa>{GAME_LABEL[mesa.gameType]}</SeloDeMesa>

          <div className="flex -space-x-2">
            {mesa.jogadores.map((j) => (
              <Avatar
                key={j.userId}
                avatar={j.avatar}
                cor={j.avatarColor}
                tamanho={40}
                nome={j.displayName}
                // O anel separa um avatar do outro quando se sobrepoem.
                // Sem ele, dois personagens de cor parecida viram uma
                // mancha so.
                className="outline-2 outline-white/60"
              />
            ))}
          </div>
          <p className="font-semibold truncate">
            {mesa.jogadores.map((j) => j.displayName).join(' × ')}
          </p>

          <BarraDeMesa comSeta={false}>
            {mesa.status === 'IN_PROGRESS' ? 'Em jogo' : 'Combinando o valor'}
          </BarraDeMesa>
        </div>
      </div>
    </li>
  );
}

function Modo({
  atual,
  onTrocar,
}: {
  atual: 'REAL' | 'VIRTUAL';
  onTrocar: (m: 'REAL' | 'VIRTUAL') => void;
}) {
  return (
    <div
      className="inline-flex rounded-lg p-1 gap-1"
      style={{ background: 'var(--muted)' }}
      role="radiogroup"
      aria-label="Modo de jogo"
    >
      {(
        [
          ['VIRTUAL', 'Treino'],
          ['REAL', 'Valendo'],
        ] as const
      ).map(([valor, rotulo]) => (
        <button
          key={valor}
          type="button"
          role="radio"
          aria-checked={atual === valor}
          onClick={() => onTrocar(valor)}
          className="rounded-md px-4 py-1.5 text-sm font-semibold transition"
          style={
            atual === valor
              ? { background: 'var(--card)', color: 'var(--foreground)', boxShadow: 'var(--shadow-card)' }
              : { color: 'var(--muted-foreground)' }
          }
        >
          {rotulo}
        </button>
      ))}
    </div>
  );
}

export function Salao({
  token,
  stakeMode,
  onTrocarModo,
  saldoRealCents,
  onAbrirMesa,
  onCarteira,
  onPartida,
}: {
  token: string;
  stakeMode: 'REAL' | 'VIRTUAL';
  onTrocarModo: (m: 'REAL' | 'VIRTUAL') => void;
  saldoRealCents: number;
  /** Abrir a própria mesa continua sendo a fila: entra e espera. */
  onAbrirMesa: () => void;
  onCarteira: () => void;
  onPartida: (matchId: string, gameType: GameType, aposta: EstadoAposta | null) => void;
}) {
  const [salao, setSalao] = useState<SalaoDados | null>(null);
  const [agora, setAgora] = useState(() => Date.now());
  const [sentando, setSentando] = useState(false);

  // A troca de tela não pode acontecer duas vezes: o WebSocket da partida
  // seria aberto em dobro.
  const encerrado = useRef(false);
  const onPartidaRef = useRef(onPartida);
  onPartidaRef.current = onPartida;

  const recarregar = useCallback(async (): Promise<void> => {
    const resultado = await carregarSalao(token, stakeMode);
    if (resultado.estado === 'ok') setSalao(resultado.dado);
  }, [token, stakeMode]);

  useEffect(() => {
    // Trocar de modo é trocar de sala: a lista anterior não vale mais, e
    // deixá-la na tela mostraria mesas em que não dá para sentar.
    setSalao(null);
    void recarregar();
    const t = setInterval(() => {
      setAgora(Date.now());
      void recarregar();
    }, INTERVALO_MS);
    return () => clearInterval(t);
  }, [recarregar]);

  /**
   * `gameType` vem do cartão clicado, e não de uma busca posterior na
   * lista.
   *
   * Sentar tira a mesa das abertas, e o salão recarrega sozinho a cada
   * poucos segundos. Procurar a mesa depois da chamada era uma corrida
   * que a atualização vencia com frequência: `find` devolvia nada, o
   * código chutava jogo da velha, e quem sentou num xadrez recebia
   * `estado invalido` ao abrir o tabuleiro errado.
   *
   * O jogo de uma mesa não muda enquanto ela existe — se mudou de dono,
   * `sentar` recusa. Então o valor lido no instante do clique é o certo,
   * e não há caso em que ele falte.
   */
  async function aoSentar(hostId: string, gameType: GameType): Promise<void> {
    if (encerrado.current) return;
    setSentando(true);
    const resultado = await sentar(token, hostId);
    setSentando(false);

    if (resultado.estado === 'ok') {
      encerrado.current = true;
      // A mesa acabou de nascer: ainda não há proposta de valor, e a tela
      // de negociação carrega o estado por conta própria.
      onPartidaRef.current(resultado.dado.matchId, gameType, null);
      return;
    }

    if (resultado.estado === 'recusado') {
      avisar.erro('Não deu para sentar', resultado.mensagem);
      // Perder a corrida é comum: recarrega para a lista refletir quem
      // ainda está lá, em vez de deixar a mesa fantasma na tela.
      void recarregar();
      return;
    }
    avisar.erro('O serviço não respondeu', 'Tente de novo em instantes.');
  }

  const abertas = salao?.abertas ?? [];
  const ocupadas = salao?.ocupadas ?? [];
  const vazio = salao !== null && abertas.length === 0 && ocupadas.length === 0;
  const valendo = stakeMode === 'REAL';
  const semSaldoReal = valendo && saldoRealCents <= 0;

  return (
    <main className="mx-auto w-full max-w-4xl px-4 py-8 grid gap-8 content-start grow">
      <div className="grid gap-4">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-bold" style={{ fontSize: 'var(--text-title)' }}>
              Salão
            </h1>
            <p className="text-muted-foreground">
              {salao === null
                ? 'Procurando mesas…'
                : vazio
                  ? 'Ninguém por aqui agora.'
                  : `${abertas.length} ${abertas.length === 1 ? 'mesa aberta' : 'mesas abertas'}`}
            </p>
          </div>
          <Modo atual={stakeMode} onTrocar={onTrocarModo} />
        </div>

        <p className="text-sm text-muted-foreground">
          {valendo
            ? 'Partidas com dinheiro de verdade. O valor fica retido até a partida terminar, e empate devolve a entrada inteira.'
            : 'Partidas com saldo de treino. Nada aqui envolve dinheiro, e nada conta no seu histórico.'}
        </p>
      </div>

      {/* Faltar saldo não esconde a sala: mostra o que falta. Quem chega
          sem depósito precisa saber que o caminho existe. */}
      {semSaldoReal ? (
        <section className="card p-5 grid gap-3">
          <h2 className="font-semibold">Você ainda não tem saldo</h2>
          <p className="text-muted-foreground text-sm">
            Para jogar valendo é preciso depositar. Você pode abrir mesa e jogar no treino
            enquanto isso — as regras e o relógio são os mesmos.
          </p>
          <div className="flex flex-wrap gap-2">
            <button type="button" className="btn btn-primary" onClick={onCarteira}>
              Depositar
            </button>
            <button type="button" className="btn btn-ghost" onClick={() => onTrocarModo('VIRTUAL')}>
              Jogar no treino
            </button>
          </div>
        </section>
      ) : null}

      {vazio && !semSaldoReal ? (
        <section className="card p-8 grid gap-3 text-center">
          <p style={{ fontSize: 'var(--text-lead)' }}>
            {valendo ? 'Nenhuma mesa valendo agora.' : 'O salão de treino está vazio.'}
          </p>
          <p className="text-muted-foreground">
            Abra sua mesa e escolha o jogo: quem chegar depois senta com você. Enquanto
            espera, dá para fechar a página — a mesa continua aberta.
          </p>
          <button
            type="button"
            className="btn btn-primary justify-self-center !px-6"
            onClick={onAbrirMesa}
          >
            Abrir minha mesa
          </button>
        </section>
      ) : null}

      {abertas.length > 0 ? (
        <section className="grid gap-3">
          <div className="flex items-center justify-between gap-4">
            <h2 className="rotulo">Mesas abertas</h2>
            <button type="button" className="btn btn-ghost !px-3 !py-1.5" onClick={onAbrirMesa}>
              Abrir minha mesa
            </button>
          </div>
          <ul className="grid gap-3 sm:grid-cols-2">
            {abertas.map((m) => (
              <MesaLivre
                key={m.hostId}
                mesa={m}
                agora={agora}
                ocupado={sentando}
                onSentar={(id, jogo) => void aoSentar(id, jogo)}
              />
            ))}
          </ul>
        </section>
      ) : null}

      {ocupadas.length > 0 ? (
        <section className="grid gap-3">
          <h2 className="rotulo">Em jogo ({ocupadas.length})</h2>
          <ul className="grid gap-3 sm:grid-cols-2">
            {ocupadas.map((m) => (
              <MesaEmJogo key={m.matchId} mesa={m} />
            ))}
          </ul>
          <p className="text-xs text-muted-foreground">
            Partidas em andamento. Não dá para entrar numa mesa ocupada.
          </p>
        </section>
      ) : null}
    </main>
  );
}
